import { type ReactElement, type ReactNode } from 'react'
import { LabelDetails } from 'app/components/terros'
import { TButton } from 'app/components/buttons'
import { isNotEmpty } from '@terros/common'
import { Accordion, Group, Stack } from '@mantine/core'
import { Size, useTranslation } from '@front/model'
import { type GroupedColumnDefs } from '../util'
import { type TableColumnDef } from '../types'

type Props<T extends TableColumnDef> = GroupedColumnDefs<T> & {
  selectedColumns: string[]
  renderColumn: (column: T) => ReactNode
  toggleGroup: (group: string, enabled: boolean) => void
}

export const GroupedColumns = <T extends TableColumnDef>(props: Props<T>): ReactElement => {
  const { other, groups, renderColumn, toggleGroup } = props
  return (
    <Accordion>
      {isNotEmpty(other.columns) && (
        <ColumnGroup
          group='other'
          selected={other.selected}
          toggleGroup={toggleGroup}
          columns={other.columns}
          renderColumn={renderColumn}
        />
      )}
      {Array.from(groups.entries()).map(([group, { columns, selected }]) => {
        return (
          <ColumnGroup
            key={group}
            group={group}
            selected={selected}
            toggleGroup={toggleGroup}
            columns={columns}
            renderColumn={renderColumn}
          />
        )
      })}
    </Accordion>
  )
}

type GroupProps<T extends TableColumnDef> = {
  group: string
  selected: number
  toggleGroup: (group: string, enabled: boolean) => void
  columns: T[]
  renderColumn: (column: T) => ReactNode
}
const ColumnGroup = <T extends TableColumnDef>({
  group,
  selected,
  toggleGroup,
  columns,
  renderColumn,
}: GroupProps<T>): ReactElement => {
  const { t } = useTranslation()
  return (
    <Accordion.Item value={group} key={group}>
      <Accordion.Control className='gap-4'>
        <Group justify='space-between' wrap='nowrap'>
          <LabelDetails label={group} details={selected ? t('{{count}} selected', { count: selected }) : undefined} />
          <TButton
            variant={selected ? 'destructive' : 'secondary'}
            onClick={(e) => {
              e.stopPropagation()
              toggleGroup(group, !selected)
            }}
            size='xs'
          >
            {selected ? t('Deselect All') : t('Select All')}
          </TButton>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap={Size.space.xl}>{columns.map((col) => renderColumn(col))}</Stack>
      </Accordion.Panel>
    </Accordion.Item>
  )
}
